// src/modules/leads/pages/LeadSourcesAdmin.jsx
// Admin-configurable lead sources (where a lead came from). Drives the Source dropdown.
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Space, Tag, message, Typography, Popconfirm } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchLeadSources, createLeadSource, updateLeadSource, deleteLeadSource } from '../api';

const { Title } = Typography;

export default function LeadSourcesAdmin() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await fetchLeadSources(); setSources(r.data.data || []); }
    catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); form.resetFields(); setOpen(true); };
  const openEdit = (row) => { setEditing(row); form.setFieldsValue(row); setOpen(true); };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      if (editing) await updateLeadSource(editing._id, v);
      else await createLeadSource(v);
      message.success(editing ? 'Updated' : 'Created');
      setOpen(false); load();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', render: (v) => <Tag>{v}</Tag> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Description', dataIndex: 'description', render: (v) => v || '—' },
    { title: 'Active', dataIndex: 'isActive', render: (v) => v ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag> },
    {
      title: '', render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          {r.isActive && <Popconfirm title="Deactivate this source?" onConfirm={async () => { await deleteLeadSource(r._id); message.success('Deactivated'); load(); }}><Button size="small" danger>Deactivate</Button></Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Lead Sources</Title>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={openNew}>New Source</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </Space>
      <Card size="small">
        <Table rowKey="_id" size="small" columns={columns} dataSource={sources} loading={loading} pagination={false} />
      </Card>

      <Modal title={editing ? 'Edit Lead Source' : 'New Lead Source'} open={open} onCancel={() => setOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input disabled={!!editing} placeholder="e.g. REFERRAL" /></Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Referral" /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
